import React, { useEffect, useRef, useState } from 'react';
import apiService from '../services/api';
import { ReactComponent as TicketIcon } from '../assets/icons/ticket-entries.svg';
import {
  countdownTargetMs,
  formatDisplayNumber,
  periodCountdownLabel,
  shouldShowDream,
} from '../utils/dreamSummary';
import '../styles/DreamEntries.css';

function DreamEntriesCard({ summary, now }) {
  if (!shouldShowDream(summary)) return null;
  const { period } = summary;
  const countdown = periodCountdownLabel(period, now);
  return (
    <section className="dream-entries" aria-label="Dream Prize entries">
      <div className="dream-entries-balance">
        <span className="dream-entries-label">
          <TicketIcon className="dream-entries-icon" aria-hidden="true" />
          Your entries in this draw
        </span>
        <strong className="dream-entries-num">{formatDisplayNumber(summary.entries)}</strong>
      </div>
      <div className="dream-entries-period">
        <div className="dream-entries-period-head">
          <span className="dream-entries-period-name">{period.name}</span>
          {countdown ? <span className="dream-entries-countdown">{countdown}</span> : null}
        </div>
        <p className="dream-entries-reset">Entries reset after the drawing</p>
        {summary.rules_url ? (
          <a className="dream-entries-rules" href={summary.rules_url} target="_blank" rel="noreferrer">
            Official rules
          </a>
        ) : null}
      </div>
    </section>
  );
}

// Loads the same Dream summary the embed reads. Until it arrives, and when the
// first request fails, nothing renders, so the page never shows a balance of 0.
// A failed read again later keeps the block that is on screen.
function DreamEntries() {
  const [summary, setSummary] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [loads, setLoads] = useState(0);
  const awaited = useRef(null);
  const visible = shouldShowDream(summary);
  const target = visible ? countdownTargetMs(summary.period, now) : null;

  useEffect(() => {
    let cancelled = false;
    apiService
      .getDreamSummary()
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [loads]);

  // A countdown seen running reads a fresh summary once it runs out: the period
  // may have been extended or drawn meanwhile.
  useEffect(() => {
    if (target == null) return;
    if (target > now) {
      awaited.current = target;
    } else if (awaited.current === target) {
      awaited.current = null;
      setLoads((n) => n + 1);
    }
  }, [target, now]);

  useEffect(() => {
    if (!visible) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [visible]);

  return <DreamEntriesCard summary={summary} now={now} />;
}

export default DreamEntries;
